/**
 * CurrencyInput — text input that displays numbers with thousand-separator commas.
 *
 * Accepts / emits the raw numeric string (no commas) so existing patterns
 * like parseFloat(form.field) keep working without any changes to form state.
 *
 * Usage (drop-in for <input type="number"> on VND fields):
 *   <CurrencyInput
 *     value={form.monthly_rent}
 *     onChange={e => setForm(f => ({ ...f, monthly_rent: e.target.value }))}
 *     className={fieldCls}
 *     placeholder="0"
 *   />
 */
import React from 'react';

const addCommas = (v) => {
  const digits = String(v ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const onlyDigits = (v) => String(v ?? '').replace(/\D/g, '');

export function CurrencyInput({
  value,
  defaultValue,
  onChange,
  name,
  id,
  className,
  disabled,
  placeholder,
  required,
  autoComplete,
  style,
  onBlur,
  onFocus,
  'aria-label': ariaLabel,
  // strip type/min/max/step — not valid on type="text"
  type: _type,
  min: _min,
  max: _max,
  step: _step,
  ...rest
}) {
  // Uncontrolled only when defaultValue is explicitly passed (FormData-based forms);
  // otherwise stay controlled exactly as before. Keying off defaultValue (not the current
  // value) avoids any controlled↔uncontrolled switch for existing value+onChange callers.
  const isUncontrolled = defaultValue !== undefined;
  const [internal, setInternal] = React.useState(onlyDigits(defaultValue));
  const current = isUncontrolled ? internal : value;

  const handleChange = (e) => {
    const raw = onlyDigits(e.target.value);
    if (isUncontrolled) setInternal(raw);
    onChange?.({ ...e, target: { ...e.target, value: raw, name } });
  };

  return (
    <>
    {/* Hidden field carries the raw numeric value so FormData/parseFloat get a clean
        number (the visible input shows a comma-formatted string). */}
    {name && <input type="hidden" name={name} value={onlyDigits(current)} readOnly />}
    <input
      {...rest}
      id={id}
      type="text"
      inputMode="numeric"
      value={addCommas(current)}
      onChange={handleChange}
      onBlur={onBlur}
      onFocus={onFocus}
      className={className}
      disabled={disabled}
      placeholder={placeholder}
      required={required}
      autoComplete={autoComplete ?? 'off'}
      style={style}
      aria-label={ariaLabel}
    />
    </>
  );
}
